/**
 * Card M3 (dashboard spec §2.2): the dashboard's Log › Comms section body. It shows the open conversation (the comms
 * panel) or, with none open, the waiting hail's Answer button. Replies are `FocusButton`s driven by the shell's focus
 * inputs (`useDashFocus`). Leaving the section (another tab or section) closes the conversation without a reply, so
 * it waits again at its node, as Esc did on the old panel.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { DashSectionComponent, DashSectionProps } from '../../ui/dashboard/registry'
import { useDashFocus } from '../../ui/dashboard/useDashFocus'
import { FocusContext } from '../../ui/focus'
import type { FocusGroup } from '../../ui/nav'
import { CommsPanel } from './CommsPanel'
import type { CommsLabels, PanelModel } from './model'

/** What the section reads from and sends to the comms feature. */
export interface CommsSectionSource {
  readonly labels: CommsLabels
  /** The open conversation's panel model (null: none open). */
  model(): PanelModel | null
  /** The waiting hail's Answer text ("<name> is hailing · [O] Answer"), or null when none waits. */
  answerText(): string | null
  answer(): void
  reply(id: string): void
  /** Leaves the section: closes the open conversation without a reply. */
  leave(): void
  /** Calls `fn` when the session changes; returns the unsubscribe. */
  subscribe(fn: () => void): () => void
}

export function createCommsSection(src: CommsSectionSource): DashSectionComponent {
  function CommsSection(p: DashSectionProps): ReactNode {
    const [, setTick] = useState(0)
    useEffect(() => {
      const off = src.subscribe(() => setTick((n) => n + 1))
      return () => {
        off()
        src.leave()
      }
    }, [])
    const model = src.model()
    const text = model === null ? src.answerText() : null
    const count = model !== null ? model.replies.length : text !== null ? 1 : 0
    const groups: readonly FocusGroup[] = [{ id: 'comms', count }]
    const focus = useDashFocus(p.nav, groups)
    return (
      <FocusContext.Provider value={focus}>
        <CommsPanel
          model={model}
          labels={src.labels}
          onReply={(id) => src.reply(id)}
          answer={text === null ? undefined : { text, onAnswer: () => src.answer() }}
        />
      </FocusContext.Provider>
    )
  }
  return CommsSection
}
